import { AdditionalInfo } from './additional-info.model';
import { Person } from './person.model';
import { TravelHistory } from './travel-history.model';
import { DriversLicense } from './drivers-license.model';
import { Address } from './address';
import { Vehicle } from './vehicle.model';
import { Employment } from './employment';
import { Passport } from './passport.model';
import { ApplicationMinimumData } from './application-minimum-data.model';
import { Contact } from './contact.model';
import { EnhancedModel } from './enhanced.model';
import { AdditionalQuestion } from './additional-question.model';
import { CitizenshipDocuments } from './citizenship-documents.model';
import { ColombiaRequestedInfo } from './documents/colombia-requested-info.model';
import { IsraelRequestedInfo } from './documents/israel-requested-info.model';
import { MexicoRequestedInfo } from './documents/mexico-requested-info.model';
import { QatarRequestedInfo } from './documents/qatar-requested-info.model';
import { SingaporeRequestedInfo } from './documents/singapore-requested-info.model';
import { UnitedKingdomRequestedInfo } from './documents/uk-requested-info.model';
import { ArgentinaRequestedInfo } from './documents/argentina-requested-info.model';
import { BrazilRequestedInfo } from './documents/brazil-requested-info.modal';
import { DominicanRepublicRequestedInfo } from './documents/dominican-republic-requested-info.modal';
import { UnitedArabEmiratesRequestedInfo } from './documents/united-arab-emirates-requested-info.modal';
import { JordanRequestedInfo } from './documents/jordan-requested-info.modal';
import { TaiwanRequestedInfo } from './documents/taiwan-requested-info.model';
import { CostaRicaRequestedInfo } from './documents/costa-rica-requested-info.modal';
import { PilotRequestedInfo } from './documents/pilot-requested-info.model';
import { ProgramCodes, RequestType } from '../utils/enums';
import { Step } from './step';
import { TsaPreData } from './tsa-pre-data.model';
import { ApplicationPersonPhoto } from './application-photo.model';


export class Application extends EnhancedModel {

  id = 0;

  // steps required to fill out the application (to support different types of applications)
  steps: Step[] = new Array<Step>();

  // person
  person: Person = new Person();

  // program info
  programCode = '';
  programConvertInd: boolean = null;
  feeAmount = 0;

  // documents
  citizenshipDocuments: CitizenshipDocuments[] = new Array<CitizenshipDocuments>();
  citizenshipDocumentsCompleted = false; // for the front-end only
  primaryCitizenshipLPRCountryIsoCode = '';
  primaryCitizenshipCountryIsoCode = '';
  coRequestedInfo: ColombiaRequestedInfo = new ColombiaRequestedInfo();
  ilRequestedInfo: IsraelRequestedInfo = new IsraelRequestedInfo();
  mxRequestedInfo: MexicoRequestedInfo = new MexicoRequestedInfo();
  qaRequestedInfo: QatarRequestedInfo = new QatarRequestedInfo();
  sgRequestedInfo: SingaporeRequestedInfo = new SingaporeRequestedInfo();
  ukRequestedInfo: UnitedKingdomRequestedInfo = new UnitedKingdomRequestedInfo();
  arRequestedInfo: ArgentinaRequestedInfo = new ArgentinaRequestedInfo();
  twRequestedInfo: TaiwanRequestedInfo = new TaiwanRequestedInfo();
  brRequestedInfo: BrazilRequestedInfo = new BrazilRequestedInfo();
  pilotRequestedInfo : PilotRequestedInfo = new PilotRequestedInfo();
  doRequestedInfo: DominicanRepublicRequestedInfo = new DominicanRepublicRequestedInfo();
  aeRequestedInfo: UnitedArabEmiratesRequestedInfo = new UnitedArabEmiratesRequestedInfo();
  joRequestedInfo: JordanRequestedInfo = new JordanRequestedInfo();
  crRequestedInfo: CostaRicaRequestedInfo = new CostaRicaRequestedInfo();

  // vehicles
  driveBorder = '';
  vehicleAlreadyRegistered = '';
  vehicleRegisterNow = '';
  vehicles: Vehicle[] = new Array<Vehicle>();

  // address history
  addresses: Address[] = new Array<Address>();
  // diffMailing: boolean = false;
  isMailingAddressResponse: string; // response to 'is mailing same as current address'
  mailing: Address = new Address();
  contact: Contact = new Contact();

  // employment history
  employments: Employment[] = new Array<Employment>();

  // additional information
  additionalInfo: AdditionalInfo = new AdditionalInfo();

  // travel history
  travelHistory: TravelHistory = new TravelHistory();

  // driver's license
  driversLicense: DriversLicense = new DriversLicense();

  // abtc information
  passports: Passport[] = new Array<Passport>();
  addAbtc: boolean = null;
  cardReplacementCode='';

  //in the case of standalone, what program its added to
  originalMembership  = '';

  tsaPreData: TsaPreData;
  applicationPersonPhoto: ApplicationPersonPhoto ;

  status: any = null; // todo: need datatype

  // application request info
  requestStatus = '';
  requestType  = '';
  isModified = false;

  // force required validations
  forceRequired = false;

  fivLpr = '';
  fivCaseNumber = '';

  /**
   * You must construct this class with one of the listed datatypes
   * @param obj Application | ApplicationMinimumData | (json) Object
   */
  constructor(obj: any) {
    super();
    if (obj instanceof Application) {
      this.mapClone(obj);
    }
    else if (obj instanceof ApplicationMinimumData) {
      this.setupNew(obj);
    }
    else if (obj instanceof Object) {
      // this must be a json object let's deserialize it
      this.mapJson(obj);
    } else {
      throw new Error('You must specify the correct parameter');
    }
  }

  public clone():Application {
    const cloned:Application = new Application(this);
    return cloned;
  }

  public getCurrentStep(): Step {
    if (this.steps)
      {return this.steps.find(x => x.isCurrent);}
    else
      {return null;}
  }

  protected mapClone(application:Application) {
    //this.abtcPassportNumber = application.abtcPassportNumber;
    this.addAbtc = application.addAbtc;
    this.additionalInfo = application.additionalInfo;
    this.addresses = application.addresses;
    this.arRequestedInfo = application.arRequestedInfo;
    this.coRequestedInfo = application.coRequestedInfo;
    this.contact = application.contact;
    // this.diffMailing = application.diffMailing;
    this.driveBorder = application.driveBorder;
    this.driversLicense = application.driversLicense;
    this.employments = application.employments;
    this.feeAmount = application.feeAmount;
    this.id = application.id;
    this.ilRequestedInfo = application.ilRequestedInfo;
    this.primaryCitizenshipLPRCountryIsoCode = application.primaryCitizenshipLPRCountryIsoCode;
    this.mailing = application.mailing;
    this.mxRequestedInfo = application.mxRequestedInfo;
    this.person = application.person;
    this.primaryCitizenshipCountryIsoCode = application.primaryCitizenshipCountryIsoCode;
    this.programCode = application.programCode;
    this.programConvertInd = application.programConvertInd;
    this.qaRequestedInfo = application.qaRequestedInfo;
    this.sgRequestedInfo = application.sgRequestedInfo;
    this.twRequestedInfo = application.twRequestedInfo;
    this.pilotRequestedInfo = application.pilotRequestedInfo;
    this.travelHistory = application.travelHistory;
    this.ukRequestedInfo = application.ukRequestedInfo;
    this.vehicleAlreadyRegistered = application.vehicleAlreadyRegistered;
    this.vehicleRegisterNow = application.vehicleRegisterNow;
    this.vehicles = application.vehicles;
    this.citizenshipDocuments = application.citizenshipDocuments;
    this.citizenshipDocumentsCompleted = application.citizenshipDocumentsCompleted;
    this.applicationPersonPhoto = application.applicationPersonPhoto;
    this.requestType = application.requestType;
    this.requestStatus = application.requestStatus;
    this.cardReplacementCode = application.cardReplacementCode;
    this.tsaPreData = application.tsaPreData;
    this.fivLpr = application.fivLpr;
    this.fivCaseNumber = application.fivCaseNumber;
  }

  protected mapJson(jsonObject: any) {

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'requestType')) {
      this.requestType = jsonObject.requestType;
      if (RequestType.abtcRequestTypes.includes(this.requestType)) {
          this.addAbtc = false;
      }
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'requestStatus')) {
      this.requestStatus = jsonObject.requestStatus;
    }
    if (Object.prototype.hasOwnProperty.call(jsonObject, 'membershipProgramCode')) {
      this.originalMembership = jsonObject.membershipProgramCode;
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'programCodes')) {
      if (jsonObject.programCodes && jsonObject.programCodes.length === 2) {
        for (const code of jsonObject.programCodes) {
          if (code === 'AB') {
            if (!RequestType.abtcRequestTypes.includes(this.requestType)) {
              this.addAbtc = true;
            }
          }
          else {
            this.programCode = code;
          }
        }
      } else if (jsonObject.programCodes && jsonObject.programCodes.length === 1) {
        this.programCode = jsonObject.programCodes[0];
      }
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'additionalQuestions')) {
        jsonObject.additionalQuestions.forEach((question: any) => {
            // TODO: map the other questions here, check the code then
            // set the appropriate property to the resposne
            const ttpQuestion = new AdditionalQuestion(question);
            this.additionalInfo.questionList.push(ttpQuestion);

        });
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'currentAddress')) {
      const address = new Address(jsonObject.currentAddress);
      address.isCurrent = true;
      this.addresses.push(address);
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'vehicleQuestions')) {

        for (const vehicleQuestion of jsonObject['vehicleQuestions']) {
            if (vehicleQuestion.code === 'VDR') {
                this.driveBorder = vehicleQuestion.response;
            }
            if (vehicleQuestion.code === 'VAC') {
                this.vehicleAlreadyRegistered = vehicleQuestion.response;
            }
            if (vehicleQuestion.code === 'VRG') {
                this.vehicleRegisterNow = vehicleQuestion.response;
            }
        }
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'addressHistory')) {
      if (jsonObject.addressHistory !== null) {
        jsonObject.addressHistory.forEach((address: any) => {
          this.addresses.push(new Address(address));
        });
      }
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'currentEmployements')) {
      if (jsonObject.currentEmployements !== null) {
        jsonObject.currentEmployements.forEach((employment: any) => {
            const tempEmployment = new Employment(employment);
            tempEmployment.isCurrent = true;
            this.employments.push(tempEmployment);
        });
      }
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'pastEmployements')) {
      if (jsonObject.pastEmployements !== null) {
        jsonObject.pastEmployements.forEach((employment: any) => {
            const passEmploment = new Employment(employment);
            passEmploment.isCurrent = false;
            this.employments.push(passEmploment);
        });
      }
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'differentPrimaryResidenceExplantaion')) {
      // TODO: this currently doesn't map to anything
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'documentsByCitizenshipCountry')) {
      if (jsonObject.documentsByCitizenshipCountry !== null) {
        jsonObject.documentsByCitizenshipCountry.forEach((citizenshipDocuments: any) => {
          const temp : CitizenshipDocuments = new CitizenshipDocuments(citizenshipDocuments);
          this.citizenshipDocuments.push(temp);
        });
      }
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'license')) {
      this.driversLicense = new DriversLicense(jsonObject.license);
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'driverLicenseResponse')) {
      if (this.programCode === ProgramCodes.FastNorth || this.programCode === ProgramCodes.FastSouth ) {
        // default to yes for FAST
        this.driversLicense.haveLicense = 'Y';
      } else {
        this.driversLicense.haveLicense = jsonObject.driverLicenseResponse;
      }
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'id')) {
      this.id = Number(jsonObject.id);
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'partnerPropertiesDto')) {
      this.arRequestedInfo = new ArgentinaRequestedInfo(jsonObject.partnerPropertiesDto);
      this.coRequestedInfo = new ColombiaRequestedInfo(jsonObject.partnerPropertiesDto);
      this.ilRequestedInfo = new IsraelRequestedInfo(jsonObject.partnerPropertiesDto);
      this.mxRequestedInfo = new MexicoRequestedInfo(jsonObject.partnerPropertiesDto);
      this.qaRequestedInfo = new QatarRequestedInfo(jsonObject.partnerPropertiesDto);
      this.sgRequestedInfo = new SingaporeRequestedInfo(jsonObject.partnerPropertiesDto);
      this.twRequestedInfo = new TaiwanRequestedInfo(jsonObject.partnerPropertiesDto);
      this.ukRequestedInfo = new UnitedKingdomRequestedInfo(jsonObject.partnerPropertiesDto);
      this.pilotRequestedInfo = new PilotRequestedInfo(jsonObject.partnerPropertiesDto);
      this.brRequestedInfo = new BrazilRequestedInfo(jsonObject.partnerPropertiesDto);
      this.doRequestedInfo = new DominicanRepublicRequestedInfo(jsonObject.partnerPropertiesDto);
      this.aeRequestedInfo = new UnitedArabEmiratesRequestedInfo(jsonObject.partnerPropertiesDto);
      this.joRequestedInfo = new JordanRequestedInfo(jsonObject.partnerPropertiesDto);
      this.crRequestedInfo = new CostaRicaRequestedInfo(jsonObject.partnerPropertiesDto);
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'mailingAddress')) {
      this.mailing = new Address(jsonObject.mailingAddress);
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'isMailingAddressResponse')) {
          this.isMailingAddressResponse = jsonObject.isMailingAddressResponse;
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'personalInfo')) {
      this.person = new Person(jsonObject.personalInfo);
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'personUsContactDto')) {
      this.contact = new Contact(jsonObject.personUsContactDto);
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'primaryCitizenshipLPRCountryIsoCode')) {
      if (jsonObject.primaryCitizenshipLPRCountryIsoCode && jsonObject.primaryCitizenshipLPRCountryIsoCode.length > 0) {
        this.primaryCitizenshipLPRCountryIsoCode = jsonObject.primaryCitizenshipLPRCountryIsoCode;
      } else {
        this.primaryCitizenshipLPRCountryIsoCode = '';
      }
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'primaryCitizenshipCountryIsoCode')) {
      if (jsonObject.primaryCitizenshipCountryIsoCode && jsonObject.primaryCitizenshipCountryIsoCode.length > 0) {
        this.primaryCitizenshipCountryIsoCode = jsonObject.primaryCitizenshipCountryIsoCode;
      } else {
        this.primaryCitizenshipCountryIsoCode = 'US';
      }
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'programConvertInd')) {
      this.programConvertInd = jsonObject.programConvertInd;
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'status')) {
      this.status = jsonObject.status;
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'vehicles')) {
      if (jsonObject.vehicles !== null) {
        jsonObject.vehicles.forEach((vehicle: any) => {
            const tempVehicle = new Vehicle(vehicle);
          this.vehicles.push(tempVehicle);
        });
      }
    }

    this.travelHistory = new TravelHistory(jsonObject);
    if (Object.prototype.hasOwnProperty.call(jsonObject, 'visitedCountriesResponse')) {
      this.travelHistory.haveYouTraveled = jsonObject.visitedCountriesResponse;
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'cardReplacementCode')) {
      this.cardReplacementCode = jsonObject.cardReplacementCode;
    }

    if (Object.prototype.hasOwnProperty.call(jsonObject, 'tsaPreData')) {
      this.tsaPreData = new TsaPreData(jsonObject.tsaPreData);
    }


    if (Object.prototype.hasOwnProperty.call(jsonObject, 'applicationPersonPhoto')) {
      this.applicationPersonPhoto = new ApplicationPersonPhoto(jsonObject.applicationPersonPhoto);
    }


    if (Object.prototype.hasOwnProperty.call(jsonObject, 'fivLpr')) {
      this.fivLpr = jsonObject.fivLpr;
    }
    if (Object.prototype.hasOwnProperty.call(jsonObject, 'fivCaseNumber')) {
      this.fivCaseNumber = jsonObject.fivCaseNumber;
    }

  }

  protected setupNew(data: ApplicationMinimumData) {

    this.primaryCitizenshipCountryIsoCode = data.primaryCountry.isoCountryCode;
    this.programCode = data.programCode;
    const citizenship = new CitizenshipDocuments();

    citizenship.country = data.primaryCountry;
    this.citizenshipDocuments.push(citizenship);

  }

}
